import { supabase } from './supabase';
import { withTimeout } from './async';

type Severity = 'warning' | 'error' | 'fatal';
type ClientError = { severity?: Severity; code: string; message?: string; fingerprint?: string };

const recentlyReported = new Map<string, number>();

const safeMessage = (value: unknown) => {
  const message = value instanceof Error ? `${value.name}: ${value.message}` : String(value ?? '');
  return message.slice(0, 500);
};

const fingerprintFor = (code: string, message: string) => `${code}:${message}`.slice(0, 80);

export const reportClientError = async ({ severity = 'error', code, message = '', fingerprint }: ClientError) => {
  const safe = safeMessage(message);
  const key = fingerprint || fingerprintFor(code, safe);
  const previous = recentlyReported.get(key) ?? 0;
  if (Date.now() - previous < 60_000) return;
  recentlyReported.set(key, Date.now());
  try {
    const token = supabase ? (await withTimeout(supabase.auth.getSession(), 3_000).catch(() => null))?.data.session?.access_token : undefined;
    await fetch('/api/errors', {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        severity,
        code,
        route: `${window.location.pathname}${window.location.hash}`.slice(0, 160),
        message: safe,
        fingerprint: key,
        metadata: {
          appVersion: __APP_VERSION__,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          online: navigator.onLine,
        },
      }),
    });
  } catch {
    // Monitoring must never block the user flow.
  }
};

export const monitoredFetch = async (input: RequestInfo | URL, init: RequestInit | undefined, operation: string) => {
  try {
    const response = await fetch(input, init);
    if (response.status >= 500) void reportClientError({ code: `api_${response.status}`, message: operation, severity: 'error' });
    return response;
  } catch (reason) {
    void reportClientError({ code: 'api_network_error', message: `${operation}: ${safeMessage(reason)}`, severity: 'warning' });
    throw reason;
  }
};

export const installGlobalMonitoring = () => {
  window.addEventListener('error', (event) => {
    void reportClientError({ severity: 'fatal', code: 'window_error', message: event.error ?? event.message });
  });
  window.addEventListener('unhandledrejection', (event) => {
    void reportClientError({ severity: 'error', code: 'unhandled_rejection', message: event.reason });
  });
};
